// JUST FIREBASE LOGIC, HANDLES HTTP/FIREBASE REQUESTS
// IMPORTANT	--	NO MERGE WITH REDUX LOGIC --

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
  deleteDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { downloadFile } from '../../utils/helpers/blobUtils';
import { convertTimestampsToUnix } from '../../utils/dateUtils';
import { SHIFT_OPERATIONS } from '../../utils/constants/shiftConstants';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, getBlob, getStorage } from 'firebase/storage';
// Break operation constants
const BREAK_OPERATIONS = {
  START: 'startBreak',
  END: 'endBreak',
};



export const getShiftById = async (shiftId) => {
  // Validate shift ID exists
  if (!shiftId) {
    console.error('No shift ID provided for fetching shift');
    throw new Error('Shift ID is required');
  }

  try {
    // Get shift data
    const shiftDocRef = doc(db, 'shifts', shiftId);
    const docSnap = await getDoc(shiftDocRef);

    if (!docSnap.exists()) {
      console.error(`No shift found with ID: ${shiftId}`);
      throw new Error(`Shift not found with ID: ${shiftId}`);
    }

    // Get breaks data
    const breaksRef = collection(db, `shifts/${shiftId}/breaks`);
    const breaksSnapshot = await getDocs(breaksRef);
    const breaks = breaksSnapshot.docs.map((breakDoc) => ({
      id: breakDoc.id,
      ...convertTimestampsToUnix(breakDoc.data()),
    }));

    // Return shift with breaks included
    return {
      id: docSnap.id,
      ...convertTimestampsToUnix(docSnap.data()),
      breaks,
    };
  } catch (error) {
    console.error('Error fetching shift by ID:', error);
    throw new Error(`Error fetching shift by ID: ${error.message}`);
  }
};

// Get shifts for a venue within a given period, optionally filtered by employee
export const getShiftsForPeriod = async (venueId, period, employeeId) => {
  if (!venueId) {
    throw new Error('Venue is required to get shifts');
  }
  if (!period.start || !period.end) {
    throw new Error('Period is required to get shifts');
  }

  const { start, end } = period;

  const startTimestamp = Timestamp.fromDate(new Date(start));
  const endTimestamp = Timestamp.fromDate(new Date(end));

  // Build the query conditions
  const conditions = [
    where('vid', '==', venueId),
    where('start', '>=', startTimestamp),
    where('start', '<=', endTimestamp),
  ];

  // Add employee filter if provided
  if (employeeId) {
    conditions.push(where('eid', '==', employeeId));
  }

  const q = query(collection(db, 'shifts'), ...conditions);

  try {
    const querySnapshot = await getDocs(q);

    // Map the shifts to an array of objects and fetch their breaks
    const shifts = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const shift = {
          id: doc.id,
          ...convertTimestampsToUnix(doc.data()),
        };

        // Get breaks data for this shift
        const breaksRef = collection(db, `shifts/${shift.id}/breaks`);
        const breaksSnapshot = await getDocs(breaksRef);
        const breaks = breaksSnapshot.docs.map((breakDoc) => ({
          id: breakDoc.id,
          ...convertTimestampsToUnix(breakDoc.data()),
        }));

        return {
          ...shift,
          breaks,
        };
      })
    );

    return shifts;
  } catch (error) {
    console.error('Error getting shifts: ', error);
    throw new Error('Failed to fetch shifts');
  }
};

// Creates a new shift in Firestore
export const createShift = async (shiftData, vid) => {
  if (!vid) {
    throw new Error('The venue ID is required to create a shift');
  }

  try {
    // Build the shift object for Firebase
    const shiftObjectForFirebase = {
      vid,
      assignedName: null,
      assignedProfileImageUrl: null,
      clockedInAt: null,
      clockedOutAt: null,
      createdAt: Timestamp.fromDate(new Date(shiftData.createdAt)),
      start: Timestamp.fromDate(new Date(shiftData.start)),
      end: Timestamp.fromDate(new Date(shiftData.end)),
      name: shiftData.type.name,
      notes: shiftData.notes || '',
      scheduled: true,
      projectedCost: shiftData.projectedCost || 0,
      eid: null,
    };

    // Save the new shift in firestore
    const docRef = await addDoc(collection(db, 'shifts'), shiftObjectForFirebase);

    // Converts the Timestamps to Unix timestamps
    const newShiftFirebase = { id: docRef.id, ...convertTimestampsToUnix(shiftObjectForFirebase) };
    return newShiftFirebase;
  } catch (error) {
    console.error('Failed to create shift:', error);
    throw new Error('Error creating shift');
  }
};

export const updateExistingShift = async (shiftId, updatedFields) => {
  const fieldsToUpdate = { ...updatedFields };

  if (!shiftId) {
    throw new Error('Shift ID is required to update a shift');
  }

  try {
    const shiftRef = doc(db, 'shifts', shiftId);

    // Verify the shift exists
    const existingShiftSnap = await getDoc(shiftRef);
    if (!existingShiftSnap.exists()) {
      throw new Error('Shift not found in Firestore');
    }

    // Convert time-related fields to Timestamps if they exist
    if (fieldsToUpdate.createdAt) {
      fieldsToUpdate.createdAt = Timestamp.fromDate(new Date(fieldsToUpdate.createdAt));
    }
    if (fieldsToUpdate.start) {
      fieldsToUpdate.start = Timestamp.fromDate(new Date(fieldsToUpdate.start));
    }
    if (fieldsToUpdate.end) {
      fieldsToUpdate.end = Timestamp.fromDate(new Date(fieldsToUpdate.end));
    }

    // Update the document
    await updateDoc(shiftRef, fieldsToUpdate);

    // Get breaks data
    const breaksRef = collection(db, `shifts/${shiftId}/breaks`);
    const breaksSnapshot = await getDocs(breaksRef);
    const breaks = breaksSnapshot.docs.map((breakDoc) => ({
      id: breakDoc.id,
      ...convertTimestampsToUnix(breakDoc.data()),
    }));

    // Get the updated shift
    const updatedShiftSnap = await getDoc(shiftRef);
    const updatedShift = {
      id: updatedShiftSnap.id,
      ...convertTimestampsToUnix(updatedShiftSnap.data()),
      breaks,
    };

    return updatedShift;
  } catch (error) {
    console.error('Failed to update shift:', error);
    throw new Error('Error updating shift');
  }
};

export const deleteExistingShift = async (id) => {
  if (!id) {
    throw new Error('Shift ID is required to delete a shift');
  }

  try {
    const shiftRef = doc(db, 'shifts', id);
    await deleteDoc(shiftRef);
    return id;
  } catch (error) {
    console.error('Failed to delete shift:', error);
    throw new Error('Error deleting shift');
  }
};

export const autoStaffShift = async (venueId, { days }) => {
  const functions = getFunctions();
  const autoStaffingCallable = httpsCallable(functions, 'autoStaff');
  const result = await autoStaffingCallable({ vid: venueId, days });

  return result.data;
};

// Helper function to handle common shift operations (Ex: Starting or ending a shift)
const handleShiftOperation = async (shiftId, operation, validationFn, additionalParams = {}) => {
  if (!shiftId) {
    throw new Error(`Shift ID is required to ${operation} a shift`);
  }

  try {
    // First verify the shift exists
    const shiftDocRef = doc(db, 'shifts', shiftId);
    const shiftDoc = await getDoc(shiftDocRef);

    if (!shiftDoc.exists()) {
      throw new Error(`Shift not found with ID: ${shiftId}`);
    }

    const shiftData = shiftDoc.data();

    // Run custom validation if provided
    if (validationFn) {
      validationFn(shiftData);
    }

    const functions = getFunctions();
    const operationCallable = httpsCallable(functions, operation);
    const result = await operationCallable({ sid: shiftId, ...additionalParams });

    // If we get a result with an error, throw it
    if (result?.data?.error) {
      throw new Error(result.data.error.message || `Failed to ${operation} shift`);
    }

    // Fetch the updated shift data
    const updatedShiftDoc = await getDoc(shiftDocRef);
    if (!updatedShiftDoc.exists()) {
      throw new Error('Failed to fetch updated shift data');
    }

    // Get breaks data
    const breaksRef = collection(db, `shifts/${shiftId}/breaks`);
    const breaksSnapshot = await getDocs(breaksRef);
    const breaks = breaksSnapshot.docs.map((breakDoc) => ({
      id: breakDoc.id,
      ...convertTimestampsToUnix(breakDoc.data()),
    }));

    // Return the complete updated shift data
    return {
      id: updatedShiftDoc.id,
      ...convertTimestampsToUnix(updatedShiftDoc.data()),
      breaks,
    };
  } catch (error) {
    // Always ensure we have a string error message
    const errorMessage = error?.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
  }
};

export const startEmployeeShift = async (shiftId) => {
  return handleShiftOperation(shiftId, SHIFT_OPERATIONS.START, (shiftData) => {
    // Check if shift is already started
    if (shiftData.clockedInAt) {
      throw new Error('Shift is already started');
    }

    // Check if shift is assigned
    if (!shiftData.eid) {
      throw new Error('Cannot start an unassigned shift');
    }
  });
};

export const endEmployeeShift = async (shiftId) => {
  return handleShiftOperation(shiftId, SHIFT_OPERATIONS.END, (shiftData) => {
    // Check if shift is already ended
    if (shiftData.clockedOutAt) {
      throw new Error('Shift is already ended');
    }

    // Check if shift is started
    if (!shiftData.clockedInAt) {
      throw new Error('Cannot end a shift that has not been started');
    }
  });
};

// Subscribe to real-time shifts for a venue and period
export function subscribeToVenueShifts(venueId, period, employeeId, onUpdate) {
  if (!venueId || !period?.start || !period?.end) return () => { };

  const startDate = new Date(period.start);
  const endDate = new Date(period.end);

  const conditions = [where('vid', '==', venueId), where('start', '>=', startDate), where('start', '<=', endDate)];

  if (employeeId) {
    conditions.push(where('eid', '==', employeeId));
  }
  const shiftsQuery = query(collection(db, 'shifts'), ...conditions);

  const unsubscribe = onSnapshot(shiftsQuery, async (snapshot) => {
    const shifts = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const shift = {
          id: doc.id,
          ...convertTimestampsToUnix(doc.data()),
        };

        // Get breaks data for this shift (matching getShiftsForPeriod logic)
        const breaksRef = collection(db, `shifts/${shift.id}/breaks`);
        const breaksSnapshot = await getDocs(breaksRef);
        const breaks = breaksSnapshot.docs.map((breakDoc) => ({
          id: breakDoc.id,
          ...convertTimestampsToUnix(breakDoc.data()),
        }));

        return {
          ...shift,
          breaks,
        };
      })
    );
    onUpdate(shifts);
  });

  return unsubscribe;
}

// Generate PDF from given shift-ids and period
export const downloadShiftsReport = async (payload, fileName) => {
  const functions = getFunctions();
  const reportShiftSchedule = httpsCallable(functions, 'reportShiftSchedule');
  const { data } = await reportShiftSchedule(payload);

  if (data.path) {
    const storage = getStorage();
    const blob = await getBlob(ref(storage, data.path));

    downloadFile(blob, fileName, 'application/pdf');
  } else {
    throw new Error('Unable to render Shift Schedule report');
  }
};

export const publishShifts = async (newStatus, shiftIds) => {
  const functions = getFunctions();
  const publishShiftsCallable = httpsCallable(functions, 'updatePublishStatusForSchedule');
  const result = await publishShiftsCallable({ newStatus, shiftIds });
  return result.data;
};

export const unpublishShifts = async (newStatus, shiftIds) => {
  const functions = getFunctions();
  const unpublishShiftsCallable = httpsCallable(functions, 'updatePublishStatusForSchedule');
  const result = await unpublishShiftsCallable({ newStatus, shiftIds });
  return result.data;
};

export const validateShiftAll = async (start, end, eids, venueId) => {
  const functions = getFunctions();
  const validateShiftCallable = httpsCallable(functions, 'validateShift');
  const result = await validateShiftCallable({ start, end, eids, vid: venueId });
  return result.data;
};

export const validateShift = async (start, end, eid, venueId) => {
  const result = await validateShiftAll(start, end, [eid], venueId);
  return result[eid] ?? [];
};

// Start a break for an employee
export const startEmployeeBreak = async (shiftId, breakType) => {
  return handleShiftOperation(shiftId, BREAK_OPERATIONS.START, (shiftData) => {
    // Check if shift is started
    if (!shiftData.clockedInAt) {
      throw new Error('Cannot start break for a shift that has not been started');
    }
    // Check if shift is already ended
    if (shiftData.clockedOutAt) {
      throw new Error('Cannot start break for an ended shift');
    }
  }, { breakType });
};

// End a break for an employee
export const endEmployeeBreak = async (shiftId, breakId) => {
  return handleShiftOperation(shiftId, BREAK_OPERATIONS.END, (shiftData) => {
    // Check if shift is started
    if (!shiftData.clockedInAt) {
      throw new Error('Cannot end break for a shift that has not been started');
    }
    // Check if shift is already ended
    if (shiftData.clockedOutAt) {
      throw new Error('Cannot end break for an ended shift');
    }
  }, { bid: breakId });
};

export const cloneSchedule = async (fromDate, toDate) => {
  const functions = getFunctions();
  const operationCallable = httpsCallable(functions, 'cloneSchedule');
  const result = await operationCallable({ fromDate, toDate });
  return result;
};
